import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import { FaImage, FaEdit, FaTrash, FaEye } from 'react-icons/fa';

const AdminBannerEditor = () => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [existingContent, setExistingContent] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    // Fetch existing content on component mount
    useEffect(() => {
        fetchExistingContent();
    }, []);

    const fetchExistingContent = async () => {
        try {
            const response = await axios.get('https://madhusewingmachines.com/api/content1');
            // Check if response.data is an array, if not, wrap it in an array
            if (response.data) {
                if (Array.isArray(response.data)) {
                    setExistingContent(response.data);
                } else {
                    // If it's a single object, wrap it in an array
                    setExistingContent([response.data]);
                    console.log("Received single content object, converted to array:", [response.data]);
                }
            } else {
                // If no data, set empty array
                setExistingContent([]);
                console.log("No content data received, setting empty array");
            }
        } catch (err) {
            console.error('Error fetching content:', err);
            setError('Failed to load existing content');
            // Ensure existingContent is an array even on error
            setExistingContent([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.title || !formData.description || (!formData.image && !isEditing)) {
            return setError('Please fill all fields and select an image');
        }

        setIsLoading(true);

        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('description', formData.description);
            if (formData.image) {
                data.append('image', formData.image);
            }

            let response;
            if (isEditing && editId) {
                response = await axios.put(
                    `https://madhusewingmachines.com/api/content1/${editId}`,
                    data,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                setSuccess('Content updated successfully!');
            } else {
                response = await axios.post(
                    'https://madhusewingmachines.com/api/content1',
                    data,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                setSuccess('Content created successfully!');
            }

            // Reset form after successful submission
            setFormData({
                title: '',
                description: '',
                image: null
            });
            setImagePreview('');
            setIsEditing(false);
            setEditId(null);
            
            // Refresh content list
            fetchExistingContent();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save content');
            console.error('Error saving content:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (content) => {
        setFormData({
            title: content.title,
            description: content.description,
            image: null // We don't set the image here as we can't retrieve the file
        });
        setImagePreview(`https://madhusewingmachines.com/uploads/${content.image}`);
        setIsEditing(true);
        setEditId(content._id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                await axios.delete(`https://madhusewingmachines.com/api/content1/${id}`);
                setSuccess('Content deleted successfully!');
                fetchExistingContent();
            } catch (err) {
                setError('Failed to delete content');
                console.error('Error deleting content:', err);
            }
        }
    };

    const handleClear = () => {
        setFormData({
            title: '',
            description: '',
            image: null
        });
        setImagePreview('');
        setError('');
        setSuccess('');
        setIsEditing(false);
        setEditId(null);
    };

    return (
        <Container fluid className="py-5 bg-light">
            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <Card className="shadow-lg border-0 rounded-lg mb-5">
                        <Card.Header className="bg-primary text-white">
                            <h3 className="m-0 text-center">
                                {isEditing ? 'Update Content' : 'Create New Content'}
                            </h3>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
                            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={7}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">Title*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                placeholder="Enter content title"
                                                className="border-primary"
                                                required
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">Description*</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Enter content description"
                                                className="border-primary"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold">Image*</Form.Label>
                                            <div className="d-flex flex-column align-items-center">
                                                {imagePreview ? (
                                                    <div className="position-relative mb-3 image-container">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            className="img-fluid rounded border"
                                                            style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                                                        />
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm" 
                                                            className="position-absolute top-0 end-0 m-2"
                                                            onClick={() => {
                                                                setImagePreview('');
                                                                setFormData(prev => ({...prev, image: null}));
                                                            }}
                                                        >
                                                            ×
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="text-center p-4 bg-light rounded border mb-3 w-100">
                                                        <FaImage size={40} className="text-secondary mb-2" />
                                                        <p className="text-muted mb-0">No image selected</p>
                                                    </div>
                                                )}
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    className="border-primary"
                                                    required={!isEditing}
                                                />
                                                <Form.Text className="text-muted">
                                                    Recommended dimensions: 1920×1080px (Max 5MB)
                                                </Form.Text>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-between mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                        size="lg"
                                    >
                                        {isEditing ? 'Cancel' : 'Clear'}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isLoading}
                                        size="lg"
                                    >
                                        {isLoading ? (
                                            <span className="d-flex align-items-center">
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                {isEditing ? 'Updating...' : 'Creating...'}
                                            </span>
                                        ) : (isEditing ? 'Update Content' : 'Create Content')}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Existing Content Section */}
                    <Card className="shadow-lg border-0 rounded-lg">
                        <Card.Header className="bg-primary text-white">
                            <h3 className="m-0 text-center">Existing Content</h3>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {!existingContent || existingContent.length === 0 ? (
                                <Alert variant="info">No content has been created yet.</Alert>
                            ) : (
                                <Row>
                                    {existingContent.map((content) => (
                                        <Col md={6} lg={4} key={content._id || Math.random()} className="mb-4">
                                            <Card className="h-100 shadow-sm hover-card">
                                                <div className="position-relative">
                                                    <Card.Img 
                                                        variant="top" 
                                                        src={`https://madhusewingmachines.com/uploads/${content.image}`}
                                                        alt={content.title}
                                                        style={{ height: '180px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            console.error("Image failed to load:", content.image);
                                                            e.target.src = "https://via.placeholder.com/400x180?text=Image+Not+Available";
                                                            e.target.onerror = null;
                                                        }}
                                                    />
                                                    <div className="content-overlay position-absolute top-0 end-0 p-2">
                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="me-1 rounded-circle"
                                                            onClick={() => handleEdit(content)}
                                                        >
                                                            <FaEdit />
                                                        </Button>
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm" 
                                                            className="rounded-circle"
                                                            onClick={() => handleDelete(content._id)}
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <Card.Body>
                                                    <Card.Title className="text-truncate">{content.title}</Card.Title>
                                                    <Card.Text className="text-muted" style={{ 
                                                        display: '-webkit-box', 
                                                        WebkitLineClamp: 3, 
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {content.description}
                                                    </Card.Text>
                                                </Card.Body>
                                                <Card.Footer className="bg-white border-0">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm" 
                                                        className="w-100"
                                                        onClick={() => window.open(`/content/${content._id}`, '_blank')}
                                                    >
                                                        <FaEye className="me-1" /> Preview on Website
                                                    </Button>
                                                </Card.Footer>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <style jsx>{`
                .hover-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .hover-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
                }
                .content-overlay {
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                .hover-card:hover .content-overlay {
                    opacity: 1;
                }
                .image-container {
                    transition: transform 0.3s ease;
                }
                .image-container:hover {
                    transform: scale(1.02);
                }
            `}</style>
        </Container>
    );
};

export default AdminBannerEditor;
